import { z } from 'zod';
import { noyauCodeSchema } from './noyau.js';

/**
 * What a SESSION records of its REVIEW, « Relecture » on screen (relecture.md,
 * issue #187): a notebook SHARED by the operators, one per session, where one
 * files the matter by hand, annotates the interviewer's questions, validates a
 * colleague's filing and sets the noyau one reads.
 *
 * It is posed BESIDE the machine and never replaces it (§2): nothing here feeds
 * the interviewer, the scribe, the ranger or the real determination. The one
 * reader outside the page is the blank analyst, on the source « Relecture »
 * (§11).
 *
 * ## Vocabulary
 *
 * - **Turn number**: 1-BASED, the one « Tour N » prints and the ranger's
 *   extracts carry (`turn`), where `SessionTurn.index` is 0-based.
 * - **Position of an extract**: `start` and `end`, in UTF-16 code units of the
 *   `reply` of that turn (`end` exclusive), and `text` is exactly
 *   `reply.slice(start, end)`. A reply never moves, so a position stays right.
 * - **Overlap**: same `turn` and `a.start < b.end && b.start < a.end`
 *   (`extractsOverlap`, in `review/rules.ts`).
 * - **Author**: the email `versionAuthor` renders (or `MACHINE_AUTHOR`, « jeton
 *   partagé »). `via` is `'claude'` for a gesture of the MCP assistant, and
 *   `dapres` the free text that says whose judgement it carries - required when
 *   `via === 'claude'` (the body of the write route demands it, below), null
 *   otherwise. Claude never signs with another operator's email (§14.2).
 *
 * ## The routes (lot A of #187 writes them; written here so the screens and the
 * MCP can code against the contract before they exist)
 *
 * - `GET /api/sessions/{id}/review` - `sessionReviewSchema`. Readable on a
 *   session in the trash (§3.4).
 * - `POST /api/sessions/{id}/review/operations` -
 *   `reviewOperationsBodySchema` in, `reviewOperationsResultSchema` out. 409
 *   `reviewRefusalSchema` when the WHOLE request is refused (a session in the
 *   trash or in progress, no protocol page); a refusal that bears on ONE
 *   operation is a line of `rejected` and the others pass.
 * - `GET /api/sessions/{id}/review/events` - `reviewEventListSchema`, newest
 *   first.
 *
 * None of the STORED shapes is `.strict()`, like every stored shape (§4.1): a
 * field added later must not make a row already written unreadable. The one
 * `.strict()` is the answer of the read route.
 */

/* ------------------------------------------------------------------ bounds */

export const REVIEW_NOTE_MAX = 2_000;
export const REVIEW_TEXT_MAX = 4_000;
export const REVIEW_DAPRES_MAX = 200;
export const REVIEW_OPERATIONS_MAX = 200;

/* ---------------------------------------------------------------- the grid */

/**
 * One case of the grid: a TARGET objective of the Rangement section of a
 * protocol version (scenario-succes.md §4), by the very identifier the ranger
 * files onto - which is what makes the comparison in a Claude conversation
 * direct.
 */
export const reviewGridObjectiveSchema = z.object({
  objectiveId: z.string().min(1),
  name: z.string().min(1),
});
export type ReviewGridObjective = z.infer<typeof reviewGridObjectiveSchema>;

/** Where the cases came from (§5.1): the version PLAYED, or by default its DEVELOPMENT version. */
export const reviewGridSourceSchema = z.enum(['played', 'development']);
export type ReviewGridSource = z.infer<typeof reviewGridSourceSchema>;

/**
 * The grid as it is FROZEN on the review at its first gesture (§5.1) - the very
 * object `session_reviews.grid` stores: the version that gave it, the targets
 * with their names and their order, and the setting « un passage, un seul
 * objectif » of THAT version (§5.4, D26). A later version of the protocol does
 * not move it.
 *
 * `oneObjectivePerPassage` is read by default (BUILD-787): a grid frozen before
 * the setting existed reads it unticked.
 */
export const reviewGridBodySchema = z.object({
  protocolId: z.string().min(1),
  protocolVersionId: z.string().min(1),
  /** The number of the version, the one the screens print. */
  version: z.number().int().positive(),
  source: reviewGridSourceSchema,
  /** In the order of the protocol; at least one, or there is no grid at all. */
  objectives: z.array(reviewGridObjectiveSchema).min(1),
  oneObjectivePerPassage: z.boolean().default(false),
});
export type ReviewGridBody = z.infer<typeof reviewGridBodySchema>;

/**
 * The grid as the read serves it: the frozen body, and whether it IS frozen -
 * `false` while no gesture has been made (the grid is then read from the
 * protocol of the session and may still change), `true` with the instant and
 * the author of the first gesture.
 */
export const reviewGridSchema = reviewGridBodySchema.extend({
  frozen: z.boolean(),
  frozenAt: z.string().datetime().nullable(),
  frozenBy: z.string().min(1).nullable(),
});
export type ReviewGrid = z.infer<typeof reviewGridSchema>;

/* --------------------------------------------------------------- the stamp */

/** Who set an element, when, and - for the assistant - on whose judgement (§3.3, D2, D21). */
export const reviewViaSchema = z.enum(['claude']);
export type ReviewVia = z.infer<typeof reviewViaSchema>;

export const reviewStampSchema = z.object({
  by: z.string().min(1),
  at: z.string().datetime(),
  via: reviewViaSchema.nullable(),
  dapres: z.string().min(1).max(REVIEW_DAPRES_MAX).nullable(),
});
export type ReviewStamp = z.infer<typeof reviewStampSchema>;

/* ------------------------------------------------------------ the elements */

/**
 * One EXTRACT filed in a case (§5.2): a passage of ONE reply of the person,
 * word for word, with its turn and position, a « ? » of doubt on the placement
 * (D11) and an optional note.
 *
 * A same passage in two cases is TWO extracts, one per case (CR 20).
 */
export const reviewExtractSchema = z
  .object({
    id: z.string().uuid(),
    objectiveId: z.string().min(1),
    turn: z.number().int().min(1),
    start: z.number().int().min(0),
    end: z.number().int().min(1),
    text: z.string().min(1).max(REVIEW_TEXT_MAX),
    /** « ? » on the placement. Locked while it is the price of a shared passage (D28). */
    doubt: z.boolean(),
    note: z.string().max(REVIEW_NOTE_MAX),
  })
  .extend(reviewStampSchema.shape);
export type ReviewExtract = z.infer<typeof reviewExtractSchema>;

/** The optional note of a CASE, one per case; a note alone never makes a validation stale (D12). */
export const reviewCaseNoteSchema = z
  .object({
    objectiveId: z.string().min(1),
    note: z.string().min(1).max(REVIEW_NOTE_MAX),
  })
  .extend(reviewStampSchema.shape);
export type ReviewCaseNote = z.infer<typeof reviewCaseNoteSchema>;

export const reviewVerdictSchema = z.enum(['valid', 'invalid']);
export type ReviewVerdict = z.infer<typeof reviewVerdictSchema>;

/**
 * One validation of a case (§6): « Je valide » or « Je ne valide pas » on the
 * filing ALREADY set in the review, never on the machine's. A case keeps ALL its
 * validations.
 *
 * `current` is DERIVED and never stored (§15): it is true when the validation is
 * not older than the last modification of its case (`currentValidation`). A
 * validation that is not current reads « avant modification ».
 */
export const reviewValidationSchema = z
  .object({
    id: z.string().uuid(),
    objectiveId: z.string().min(1),
    verdict: reviewVerdictSchema,
    note: z.string().max(REVIEW_NOTE_MAX),
    current: z.boolean(),
  })
  .extend(reviewStampSchema.shape);
export type ReviewValidation = z.infer<typeof reviewValidationSchema>;

/** The state of a case, DERIVED (§6): « vide », « à valider », « à revalider », « validée », « non validée ». */
export const reviewCaseStateSchema = z.enum([
  'empty',
  'to-validate',
  'to-revalidate',
  'valid',
  'invalid',
]);
export type ReviewCaseState = z.infer<typeof reviewCaseStateSchema>;

/** The span of a message a question annotation bears on, when it is not the whole message. */
export const reviewPassageSchema = z.object({
  start: z.number().int().min(0),
  end: z.number().int().min(1),
  text: z.string().min(1).max(REVIEW_TEXT_MAX),
});
export type ReviewPassage = z.infer<typeof reviewPassageSchema>;

export const reviewQuestionVerdictSchema = z.enum(['good', 'bad']);
export type ReviewQuestionVerdict = z.infer<typeof reviewQuestionVerdictSchema>;

/**
 * An annotation of a QUESTION of the interviewer (§7, D13): « Va » or « Ne va
 * pas », required, with an optional note, on the whole message or on a passage.
 * Several per message; only a message of the interviewer is annotated.
 * `passage` positions are those of the INTERVIEWER's message of the turn.
 */
export const reviewQuestionNoteSchema = z
  .object({
    id: z.string().uuid(),
    turn: z.number().int().min(1),
    passage: reviewPassageSchema.nullable(),
    verdict: reviewQuestionVerdictSchema,
    note: z.string().max(REVIEW_NOTE_MAX),
  })
  .extend(reviewStampSchema.shape);
export type ReviewQuestionNote = z.infer<typeof reviewQuestionNoteSchema>;

export const reviewCertaintySchema = z.enum(['sure', 'doubtful']);
export type ReviewCertainty = z.infer<typeof reviewCertaintySchema>;

/**
 * The noyau of the reviewer (§8, D14): one of the six, sure or doubtful, with a
 * second candidate when doubtful, or « Pas de noyau lisible » (`unreadable`,
 * which empties the choice). One row per session; no validation of its own.
 *
 * It NEVER replaces the retained noyau (D1), and the list does not show it.
 */
export const reviewNoyauSchema = z
  .object({
    noyau: noyauCodeSchema.nullable(),
    certainty: reviewCertaintySchema,
    second: noyauCodeSchema.nullable(),
    unreadable: z.boolean(),
    note: z.string().max(REVIEW_NOTE_MAX),
  })
  .extend(reviewStampSchema.shape);
export type ReviewNoyau = z.infer<typeof reviewNoyauSchema>;

/** One free note on the session (§9, D22), edited in place and journaled. */
export const reviewNoteSchema = z
  .object({
    id: z.string().uuid(),
    text: z.string().min(1).max(REVIEW_TEXT_MAX),
  })
  .extend(reviewStampSchema.shape);
export type ReviewNote = z.infer<typeof reviewNoteSchema>;

/* ------------------------------------------------------------ the operations */

/**
 * What the screen and the assistant WRITE: one typed operation per gesture
 * (§14.2). A union on `op`, the body of `POST .../review/operations`.
 *
 * « Remettre » (§10) is NOT an operation: the front sends a `range` with the
 * fields of the `unrange` event it restores.
 *
 * - `range` - the extract is verified word for word in the reply of its turn
 *   (`review-not-verbatim`), and the setting « un seul objectif » may put a « ? »
 *   on both placements (`rangeImplies`). `start < end` and the length of `text`
 *   are the verbatim check's business, not the schema's.
 * - `note-extract`, `note-case` - an EMPTY note clears it.
 * - `edit-question-note` replaces the verdict and the note of the annotation.
 * - `add-note` and `edit-note` refuse an empty text (`review-empty-text`): the
 *   schema lets it through so the refusal is the one the screen words.
 */
export const reviewOperationSchema = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('range'),
    objectiveId: z.string().min(1),
    turn: z.number().int().min(1),
    start: z.number().int().min(0),
    end: z.number().int().min(1),
    text: z.string().max(REVIEW_TEXT_MAX),
  }),
  z.object({ op: z.literal('unrange'), extractId: z.string().uuid() }),
  z.object({ op: z.literal('set-doubt'), extractId: z.string().uuid(), doubt: z.boolean() }),
  z.object({
    op: z.literal('note-extract'),
    extractId: z.string().uuid(),
    note: z.string().max(REVIEW_NOTE_MAX),
  }),
  z.object({
    op: z.literal('note-case'),
    objectiveId: z.string().min(1),
    note: z.string().max(REVIEW_NOTE_MAX),
  }),
  z.object({
    op: z.literal('validate-case'),
    objectiveId: z.string().min(1),
    verdict: reviewVerdictSchema,
    note: z.string().max(REVIEW_NOTE_MAX),
  }),
  z.object({
    op: z.literal('annotate-question'),
    turn: z.number().int().min(1),
    passage: reviewPassageSchema.nullable(),
    verdict: reviewQuestionVerdictSchema,
    note: z.string().max(REVIEW_NOTE_MAX),
  }),
  z.object({
    op: z.literal('edit-question-note'),
    questionNoteId: z.string().uuid(),
    verdict: reviewQuestionVerdictSchema,
    note: z.string().max(REVIEW_NOTE_MAX),
  }),
  z.object({ op: z.literal('remove-question-note'), questionNoteId: z.string().uuid() }),
  z.object({
    op: z.literal('set-noyau'),
    noyau: noyauCodeSchema.nullable(),
    certainty: reviewCertaintySchema,
    second: noyauCodeSchema.nullable(),
    unreadable: z.boolean(),
    note: z.string().max(REVIEW_NOTE_MAX),
  }),
  z.object({ op: z.literal('add-note'), text: z.string().max(REVIEW_TEXT_MAX) }),
  z.object({
    op: z.literal('edit-note'),
    noteId: z.string().uuid(),
    text: z.string().max(REVIEW_TEXT_MAX),
  }),
  z.object({ op: z.literal('remove-note'), noteId: z.string().uuid() }),
]);
export type ReviewOperation = z.infer<typeof reviewOperationSchema>;
export type ReviewOperationName = ReviewOperation['op'];

/**
 * The names of the operations - what a journal event names. Written out and not
 * derived from the union: `z.enum` wants a tuple, and the test of the schemas
 * checks that the two lists are the same.
 */
export const REVIEW_OPERATION_NAMES = [
  'range',
  'unrange',
  'set-doubt',
  'note-extract',
  'note-case',
  'validate-case',
  'annotate-question',
  'edit-question-note',
  'remove-question-note',
  'set-noyau',
  'add-note',
  'edit-note',
  'remove-note',
] as const satisfies readonly ReviewOperationName[];

/**
 * Why a gesture is refused. A refusal of the WHOLE request is a 409 with this
 * code; one that bears on a single operation of a batch is a line of
 * `rejected` and the others pass (§16).
 *
 * - `session-in-trash`, `session-in-progress`, `no-protocol-page` - the session
 *   cannot be reviewed (§3.1, §3.4);
 * - `review-no-grid` - no target anywhere (§5.1), so nothing to file;
 * - `review-unknown-objective`, `review-unknown-element` - a case outside the
 *   frozen grid, an id that is not this review's;
 * - `review-not-verbatim` - the extract is not `reply.slice(start, end)`;
 * - `review-turn-out-of-range` - no such turn, or no reply of the person in it;
 * - `review-doubt-locked` - the « ? » of a shared passage under the setting (D28);
 * - `review-noyau-invalid` - a second without doubt, a second equal to the
 *   noyau, an unreadable choice that still names one (`reviewNoyauProblem`);
 * - `review-empty-text` - an empty note or text where one is required.
 */
export const reviewRefusalCodeSchema = z.enum([
  'session-in-trash',
  'session-in-progress',
  'no-protocol-page',
  'review-no-grid',
  'review-unknown-objective',
  'review-unknown-element',
  'review-not-verbatim',
  'review-turn-out-of-range',
  'review-doubt-locked',
  'review-noyau-invalid',
  'review-empty-text',
]);
export type ReviewRefusalCode = z.infer<typeof reviewRefusalCodeSchema>;

/** The 409 body, the shape of the other refusals of Sessions (`rangementTrialErrorSchema`). */
export const reviewRefusalSchema = z
  .object({
    error: z.string(),
    code: reviewRefusalCodeSchema,
  })
  .strict();
export type ReviewRefusal = z.infer<typeof reviewRefusalSchema>;

/**
 * The body of the write route. `via` is the assistant's mark: `null` for a
 * gesture of the screen, `{ dapres }` for a gesture of the MCP, and then
 * `dapres` is REQUIRED (D21) - which makes « via Claude » impossible without it.
 */
export const reviewOperationsBodySchema = z.object({
  operations: z.array(reviewOperationSchema).min(1).max(REVIEW_OPERATIONS_MAX),
  via: z
    .object({ dapres: z.string().trim().min(1).max(REVIEW_DAPRES_MAX) })
    .nullable()
    .default(null),
});
export type ReviewOperationsBody = z.infer<typeof reviewOperationsBodySchema>;

/* ------------------------------------------------------------------ the read */

/** Why the review cannot be WRITTEN right now (§3.1, §3.4). */
export const reviewIneligibleReasonSchema = reviewRefusalCodeSchema.extract([
  'session-in-progress',
  'session-in-trash',
  'no-protocol-page',
]);
export type ReviewIneligibleReason = z.infer<typeof reviewIneligibleReasonSchema>;

/** Whether a gesture would pass, and if not why; `reason` is null exactly when `canWrite` is true. */
export const reviewEligibilitySchema = z.object({
  canWrite: z.boolean(),
  reason: reviewIneligibleReasonSchema.nullable(),
});
export type ReviewEligibility = z.infer<typeof reviewEligibilitySchema>;

export const reviewCaseStateEntrySchema = z.object({
  objectiveId: z.string().min(1),
  state: reviewCaseStateSchema,
});
export type ReviewCaseStateEntry = z.infer<typeof reviewCaseStateEntrySchema>;

/**
 * `GET /api/sessions/{id}/review` - the review as it stands (§4), served WHOLE:
 * a session has a few dozen elements at most.
 *
 * - `eligibility` - the review is always readable, in the trash too (§3.4);
 * - `grid` - null when no target exists anywhere (§5.1): the page keeps the
 *   questions, the noyau and the notes;
 * - `caseStates` - one per case of the grid, DERIVED (`caseStateOf`);
 * - `authors` - the distinct emails that set something, for the mark of the list
 *   (§13), in order of first appearance.
 */
export const sessionReviewSchema = z
  .object({
    sessionId: z.string().min(1),
    eligibility: reviewEligibilitySchema,
    grid: reviewGridSchema.nullable(),
    extracts: z.array(reviewExtractSchema),
    caseNotes: z.array(reviewCaseNoteSchema),
    validations: z.array(reviewValidationSchema),
    caseStates: z.array(reviewCaseStateEntrySchema),
    questionNotes: z.array(reviewQuestionNoteSchema),
    noyau: reviewNoyauSchema.nullable(),
    notes: z.array(reviewNoteSchema),
    authors: z.array(z.string().min(1)),
  })
  .strict();
export type SessionReview = z.infer<typeof sessionReviewSchema>;

/** A refusal of ONE operation of a batch, by its index in the request. */
export const reviewRejectionSchema = z.object({
  index: z.number().int().min(0),
  code: reviewRefusalCodeSchema,
  message: z.string(),
});
export type ReviewRejection = z.infer<typeof reviewRejectionSchema>;

/** The answer of the write route: the review AFTER the gestures, and what was applied or refused. */
export const reviewOperationsResultSchema = z.object({
  review: sessionReviewSchema,
  applied: z.number().int().min(0),
  rejected: z.array(reviewRejectionSchema),
});
export type ReviewOperationsResult = z.infer<typeof reviewOperationsResultSchema>;

/* ---------------------------------------------------------------- the journal */

/**
 * One line of the JOURNAL (§10, D17): appended, never rewritten - who, when,
 * which operation, and the element BEFORE and AFTER (null on a creation or a
 * removal respectively).
 *
 * Convention the readers rely on: for `range`, `unrange` and `set-doubt`,
 * `before` and `after` carry the `ReviewExtract` row (as stored, signature
 * included) - which is how `caseChangedAt` knows the case a line touches, and
 * how « Remettre » rebuilds the `range` of an `unrange`. An extract that the
 * setting « un seul objectif » puts in doubt as a SIDE EFFECT of a `range` is
 * journaled as its own `set-doubt` line, at the instant of the `range`.
 */
export const reviewEventSchema = z.object({
  id: z.string().uuid(),
  at: z.string().datetime(),
  by: z.string().min(1),
  via: reviewViaSchema.nullable(),
  dapres: z.string().min(1).max(REVIEW_DAPRES_MAX).nullable(),
  op: z.enum(REVIEW_OPERATION_NAMES),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
});
export type ReviewEvent = z.infer<typeof reviewEventSchema>;

/** `GET /api/sessions/{id}/review/events` - newest first. */
export const reviewEventListSchema = z.object({ events: z.array(reviewEventSchema) });
export type ReviewEventList = z.infer<typeof reviewEventListSchema>;
